import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useBusyState } from "@hooks/useBusyState";
import { useConsultationManagement } from "@hooks/useConsultationManagement";
import { useConsultationRecording } from "@hooks/useConsultationRecording";
import Sidebar from "@components/consultationRecorder/Sidebar";
import MicButton from "@components/consultationRecorder/MicButton";
import ConsultationDetails from "@components/consultationRecorder/ConsultationDetails";
import RecordingControls from "@components/consultationRecorder/RecordingControls";
import ProcessingModal from "@components/consultationRecorder/ProcessingModal";

import BookedAppointmentsTable from "@components/consultationRecorder/BookedAppointmentsTable";
import ControlPanelTab from "@components/consultationRecorder/ControlPanelTab";
import RenameModal from "@components/consultationRecorder/RenameModal";
import NoSleep from "nosleep.js";
import { useUploadQueue } from "@hooks/useUploadQueue";
import { useInfiniteList } from "@hooks/useInfiniteList";
import { fetchAppointments } from "@services/appointments";
import { useLocation, useNavigate } from "react-router-dom";
import { canAccess, isOwner, isAdmin, isOutboundEnabled, canSeeReactivation } from "@utils/access";
import FrontdeskTabs from "@components/layout/FrontdeskTabs";
import { useClinicScope } from "@context/ClinicContext";
import { DEFAULT_CALLS_QUERY } from "@constants/callsQuery";

export default function ConsultationRecorder() {
  const backendUrl = process.env.REACT_APP_BACKEND_BASE_URL;

  // State management
  const busyState = useBusyState();
  const consultationManager = useConsultationManagement(backendUrl);
  const uploadQueue = useUploadQueue({
    backendUrl,
    consultationManager,
    busyState,
  });
  const recording = useConsultationRecording(
    backendUrl,
    busyState,
    consultationManager,
    uploadQueue
  );

  const {
    combinedConsultations,
    selectedConsultation,
    viewMode,
    setViewMode,
    setSelectedConsultation,
    fetchConsultations,
    deleteConsultation,
    selectConsultation,
    renameConsultation,
    error,
  } = consultationManager;

  const {
    isRecording,
    isPaused,
    processingStage,
    micVisualState,
    startRecording,
    finishRecording,
    resetRecording,
    pauseRecording,
    resumeRecording,
    continueRecording,
  } = recording;

  // UI state
  const [renameModal, setRenameModal] = useState({ open: false, item: null });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isContinuingRecording, setIsContinuingRecording] = useState(false);

  // What the calls table asks the server for: search, chip filters, sort.
  const [callsQuery, setCallsQuery] = useState(DEFAULT_CALLS_QUERY);

  // Frontdesk surface has tabs: "calls" (Frontdesk Calls) and "control"
  // (Control Panel).
  // Reactivation is its own page; its tab here only navigates there, and
  // that page's tab row sends the user back with the tab it wants opened.
  const location = useLocation();
  const [frontdeskTab, setFrontdeskTab] = useState(() => {
    const wanted = location.state?.frontdeskTab;
    return ["calls", "control"].includes(wanted) ? wanted : "calls";
  });
  const navigate = useNavigate();
  const showReactivation = useMemo(() => canSeeReactivation(), []);
  const onFrontdeskTab = (key) => (
    key === "reactivation" ? navigate("/reactivation") : setFrontdeskTab(key)
  );
  // Which clinic an admin is looking at is owned by the shared scope; the
  // picker sits in the tab row. A clinic user gets null and sends no clinic.
  const { clinicId: selectedClinicId, clinics } = useClinicScope();

  const [permTick, setPermTick] = useState(0);

  useEffect(() => {
    const onStorage = () => setPermTick((x) => x + 1);
    window.addEventListener("storage", onStorage);

    // also listen for same-tab updates (storage event doesn’t fire in same tab)
    window.addEventListener("permissions-updated", onStorage);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("permissions-updated", onStorage);
    };
  }, []);

  // These read localStorage, so permTick is the dependency that re-reads
  // them when the permissions-updated event fires.
  /* eslint-disable react-hooks/exhaustive-deps */
  const canClinical = useMemo(
    () => isOwner() || canAccess("clinical"),
    [permTick]
  );
  const canFrontdesk = useMemo(
    () => isOwner() || canAccess("frontdesk"),
    [permTick]
  );
  const Admin = useMemo(() => isAdmin(), [permTick]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // The selected clinic's row, for an admin: its outbound setting decides
  // whether the calls table offers outbound calls, and its phone flags
  // self-calls.
  const selectedClinic = useMemo(
    () => (Admin && selectedClinicId ? clinics.find((c) => c.id === selectedClinicId) : undefined),
    [Admin, selectedClinicId, clinics]
  );
  const outboundEnabled = useMemo(() => {
    if (selectedClinic) return selectedClinic.outbound_calls_enabled ?? false;
    // A clinic user's own setting comes from their login session.
    return isOutboundEnabled();
  }, [selectedClinic]);

  const noSleepRef = useRef(null);
  const handleContinueFromNote = async (rid, createdAtISO) => {
    setIsContinuingRecording(true);
    await continueRecording({ recordingId: rid, createdAtISO });
  };

  const handleStopFromNote = async () => {
    await finishRecordingWithWakeLock();
    setIsContinuingRecording(false);
  };

  useEffect(() => {
    noSleepRef.current = new NoSleep();

    return () => {
      if (noSleepRef.current) {
        noSleepRef.current.disable();
      }
    };
  }, []);
  const startRecordingWithWakeLock = useCallback(async () => {
    if (noSleepRef.current) {
      noSleepRef.current.enable();
    }
    await startRecording();
  }, [startRecording]);

  const finishRecordingWithWakeLock = async () => {
    await finishRecording();
    if (noSleepRef.current) {
      noSleepRef.current.disable();
    }
  };

  const resetRecordingWithWakeLock = async () => {
    await resetRecording();
    if (noSleepRef.current) {
      noSleepRef.current.disable();
    }
  };
  useEffect(() => {
    const handleBeforeUnload = async (event) => {
      // Only trigger for recording or active processing
      if (
        isRecording ||
        (processingStage !== "idle" && processingStage !== "done")
      ) {
        console.log("🚨 Page closing - emergency flush triggered");

        // Use synchronous-style persistence with minimal operations
        event.preventDefault();

        // Create a minimal sync operation
        const syncFlush = async () => {
          try {
            await recording.emergencyFlushAndPersist();
          } catch (error) {
            console.error("Emergency flush failed:", error);
          }
        };

        // Fire and forget with a small timeout to allow critical writes
        const flushPromise = syncFlush();

        // Wait a very short time for critical operations
        await Promise.race([
          flushPromise,
          new Promise((resolve) => setTimeout(resolve, 300)),
        ]);

        // Chrome requires this
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
    // `recording` is the whole hook result and changes identity every
    // render; the flush function is the part the handler uses.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isRecording,
    processingStage,
    recording.emergencyFlushAndPersist,
    backendUrl,
  ]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        // Page is being hidden (tab switch, app background, etc.)
        if (
          isRecording ||
          (processingStage !== "idle" && processingStage !== "done")
        ) {
          console.log("👻 Page hidden - triggering emergency save");
          recording.emergencyFlushAndPersist();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording, processingStage, recording.emergencyFlushAndPersist]);

  // The calls list: one page at a time, newest first, more as the table is
  // scrolled. The clinic is part of the query, so an admin's clinic change
  // starts the list over; leaving the frontdesk view drops it and coming
  // back fetches a fresh first page.
  const appointmentsQuery = useMemo(
    () => ({ clinicId: Admin ? selectedClinicId : undefined, ...callsQuery }),
    [Admin, selectedClinicId, callsQuery]
  );
  const appointments = useInfiniteList({
    fetchPage: fetchAppointments,
    query: appointmentsQuery,
    pageSize: 100,
    enabled: canFrontdesk && viewMode === "appointments",
  });
  const { setRows: setAppointmentRows } = appointments;
  const patchAppointmentRow = useCallback(
    (id, patch) =>
      setAppointmentRows((prev) =>
        prev.map((r) => ((r?.id || r?.appointment_id) === id ? { ...r, ...patch } : r))
      ),
    [setAppointmentRows]
  );

  useEffect(() => {
    // ✅ If user is currently on appointments but no longer has frontdesk access, kick them out
    if (viewMode === "appointments" && !canFrontdesk) {
      setSelectedConsultation(null);
      setViewMode("new");
      return;
    }

    // ✅ If user has frontdesk access, default to appointments view
    // (the calls list loads itself once the view is on).
    if (canFrontdesk) {
      setSelectedConsultation(null);
      setViewMode("appointments");
      return;
    }

    // ✅ If user has clinical only (no frontdesk), default to consultations fetch
    if (canClinical) {
      fetchConsultations();
      return;
    }

    // ✅ If user has neither, safe default
    setSelectedConsultation(null);
    setViewMode("new");
  }, [
    permTick,
    canClinical,
    canFrontdesk,
    Admin,
    viewMode,
    fetchConsultations,
    setSelectedConsultation,
    setViewMode,
  ]);

  // Memoized values
  const canStartNewConsultation = !isRecording;
  const hideTemplateSelector = isRecording;
  const micStartHandler = useMemo(
    () => (!isRecording ? startRecordingWithWakeLock : undefined),
    [isRecording, startRecordingWithWakeLock]
  );
  const handleShowAppointments = useCallback(() => {
    if (!canFrontdesk) return;

    setSelectedConsultation(null);
    setViewMode("appointments");
    setIsSidebarOpen(false);
  }, [canFrontdesk, setSelectedConsultation, setViewMode, setIsSidebarOpen]);

  return (
    <div className="flex flex-1 min-h-0 overflow-hidden">
      {viewMode !== "appointments" && (
        <Sidebar
          consultations={combinedConsultations}
          selectedItem={selectedConsultation}
          onNew={() => {
            setViewMode("new");
            setSelectedConsultation(null);
            setIsSidebarOpen(false);
          }}
          onSelect={(item) => {
            selectConsultation(item);
            setIsSidebarOpen(false);
          }}
          onDelete={async (item) => {
            try {
              await deleteConsultation(item);
            } catch (err) {
              alert(err.message);
            }
          }}
          onEdit={(item) => {
            setRenameModal({ open: true, item });
          }}
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          onShowAppointments={handleShowAppointments}
          viewMode={viewMode}
        />
      )}

      <main className={`flex-1 relative flex flex-col min-h-0 min-w-0 ${viewMode === "appointments" ? "overflow-y-auto" : "overflow-hidden"}`}>
        {viewMode !== "appointments" && (
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 lg:hidden">
            <button
              className="p-2 rounded-md hover:bg-gray-100"
              aria-label="Open sidebar"
              onClick={() => setIsSidebarOpen(true)}
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24">
                <path
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
            <span className="font-medium">Consultation Recorder</span>
            <span className="w-6 h-6" />
          </div>
        )}

        <div className={`flex-1 flex flex-col min-h-0 min-w-0 ${viewMode === "appointments" ? "" : "overflow-hidden p-6"}`}>
          {canClinical && viewMode === "new" && (
            <div className="absolute bottom-28 left-1/2 -translate-x-1/2 flex flex-col items-center space-y-6">
              <div className="pointer-events-auto">
                <MicButton
                  state={micVisualState}
                  onStart={micStartHandler}
                  disabled={!canStartNewConsultation}
                />
              </div>
              <div
                className={[
                  "transition-all duration-200",
                  hideTemplateSelector
                    ? "opacity-0 pointer-events-none select-none"
                    : "opacity-100",
                ].join(" ")}
              />
            </div>
          )}

          {viewMode === "consultation" && (
            <ConsultationDetails
              consultation={selectedConsultation}
              filesForRecording={selectedConsultation?.__siblings || []}
              consultationManager={consultationManager}
              onStartRecording={handleContinueFromNote}
              onStopRecording={handleStopFromNote}
              isContinuingRecording={isContinuingRecording}
            />
          )}

          {viewMode === "processing" && (
            <ProcessingModal
              stage={processingStage}
              name={selectedConsultation?.file_name}
              createdAt={selectedConsultation?.created_at}
            />
          )}

          {viewMode === "appointments" && (
            <div className="flex flex-col flex-1 min-h-0 min-w-0">
              {/* Frontdesk tab bar — Control Panel is always visible,
                  Reactivation when the clinic may run campaigns (it lives on
                  its own page). */}
              <FrontdeskTabs
                active={frontdeskTab}
                showReactivation={showReactivation}
                onSelect={onFrontdeskTab}
              />

              <div className="flex-1 flex flex-col min-h-0 px-6 pt-6">
                {frontdeskTab === "control" ? (
                  <ControlPanelTab clinicId={Admin ? selectedClinicId : undefined} />
                ) : (
                  <BookedAppointmentsTable
                    rows={appointments.rows}
                    loading={appointments.loading}
                    loadingMore={appointments.loadingMore}
                    hasMore={appointments.hasMore}
                    total={appointments.total}
                    counts={appointments.counts}
                    error={appointments.error}
                    onRetry={appointments.refresh}
                    onLoadMore={appointments.loadMore}
                    query={callsQuery}
                    onQueryChange={setCallsQuery}
                    sentinelRef={appointments.sentinelRef}
                    scrollRootRef={appointments.scrollRootRef}
                    onRowPatch={patchAppointmentRow}
                    isAdmin={Admin}
                    clinicPhone={selectedClinic?.clinic_phone || ""}
                    outboundEnabled={outboundEnabled}
                  />
                )}
              </div>
            </div>
          )}
        </div>
        {canClinical &&
          processingStage === "recording" &&
          isRecording &&
          !isContinuingRecording && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2">
              <RecordingControls
                isPaused={isPaused}
                onPause={pauseRecording}
                onResume={resumeRecording}
                onReset={resetRecordingWithWakeLock}
                onFinish={finishRecordingWithWakeLock}
              />
            </div>
          )}
        {renameModal.open && (
          <RenameModal
            item={renameModal.item}
            onClose={() => setRenameModal({ open: false, item: null })}
            onSave={async (newName) => {
              await renameConsultation(renameModal.item, newName);
              setRenameModal({ open: false, item: null });
            }}
            error={error}
          />
        )}
      </main>
    </div>
  );
}
